import React, { useEffect, useState } from "react";
import { Button, FormControl, InputLabel, MenuItem, Select } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import BaseDataTable from "../../components/modules/BaseDataTable";
import CreateSegmentModal from "./CreateSegmentModal";
import Loader from "../../components/elements/Loader";
import api from "../../api";
import { ReduxApplicationState } from "../../types/ReduxTypes/ReduxApplicationState";
import { errorToaster, infoToaster } from "../../helpers/toasters";
import { setAgencyAdvertisers } from "../../redux/reducers/agency";
import { setSelectedSegmentIds } from "../../redux/reducers/campaignToCreate";

const columns = [
  { key: "segment_name", label: "Name", minWidth: 170 },
  { key: "tag", label: "Tag", minWidth: 170 },
  { key: "segment_description", label: "Description", minWidth: 170 },
  { key: "create_date", label: "Created Date", minWidth: 170 },
  {
    key: "actions",
    label: "Actions",
    minWidth: 100,
  },
];

function addDisplayStyleNone(htmlTag: string): string {
  const tagRegex = /(<img[^>]*)(>)/i;
  const styleAttribute = ' style="display:none;"';
  return htmlTag.replace(tagRegex, `$1${styleAttribute}$2`);
}

export default function RetargetinSegmentsPage({
  isAttemptingToGoNext,
  setIsAttemptingToGoNext,
  handleNext,
}: {
  isAttemptingToGoNext?: boolean;
  setIsAttemptingToGoNext?: (isAttemptingToGoNext: boolean) => void;
  handleNext?: () => void;
}) {
  const [isCreateSegmentModalOpen, setIsCreateSegmentModalOpen] = useState(false);
  const [segments, setSegments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const [selectedAdvertiser, setSelectedAdvertiser] = useState("");

  const agencyId = useSelector((state: ReduxApplicationState) => state.agency.agencyId);
  const advertisers = useSelector((state: ReduxApplicationState) => state.agency.advertisers);
  const selectedSegmentIds = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedSegmentIds);
  const selectedCampaignAdvertiser = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser,
  );

  useEffect(() => {
    dispatch(setAgencyAdvertisers(agencyId));
  }, [agencyId, dispatch]);

  const getSegments = async (advertiserId: string = "") => {
    try {
      if (!advertiserId) {
        setSegments([]);
      } else {
        setIsLoading(true);
        const { payload } = await api.getSegments(advertiserId);

        setSegments(
          payload.segments.payload
            .filter((segment: any) => !segment.segment_description?.startsWith("Hashed Emails for"))
            .map((segment: any) => ({
              id: segment.segment_key,
              segment_name: segment.segment_name,
              create_date: segment.create_date,
              tag: addDisplayStyleNone(segment.tag),
              segment_description: segment.segment_description,
              actions: (
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(addDisplayStyleNone(segment.tag));
                    infoToaster("Tag copied to clipboard", 2000);
                  }}
                  variant="outlined"
                  color="primary"
                >
                  Copy to Clipboard
                </Button>
              ),
            })),
        );
      }

      setIsLoading(false);
    } catch (error) {
      const err = error as any;
      throw new Error(err.message);
    }
  };

  useEffect(() => {
    if (!selectedAdvertiser && setIsAttemptingToGoNext) return;

    if (selectedAdvertiser) getSegments(selectedAdvertiser);
  }, [selectedAdvertiser, setIsAttemptingToGoNext]);

  useEffect(() => {
    if (isAttemptingToGoNext && setIsAttemptingToGoNext && handleNext) {
      if (selectedSegmentIds.length !== 0) {
        handleNext();
        setIsAttemptingToGoNext(false);
      } else {
        errorToaster("Please, select at least one segment", 5000);
        setIsAttemptingToGoNext(false);
      }
    }
  }, [
    dispatch,
    handleNext,
    isAttemptingToGoNext,
    setIsAttemptingToGoNext,
    selectedSegmentIds,
    selectedSegmentIds.length,
  ]);

  useEffect(() => {
    if (setIsAttemptingToGoNext && selectedCampaignAdvertiser) {
      setSelectedAdvertiser(selectedCampaignAdvertiser.beeswaxAdvertiserId);
    }
  }, [setIsAttemptingToGoNext, selectedCampaignAdvertiser]);

  useEffect(() => {
    if (advertisers.length > 0 && !selectedCampaignAdvertiser) {
      setSelectedAdvertiser(advertisers[0].beeswaxAdvertiserId);
    }
  }, [advertisers, selectedCampaignAdvertiser]);

  return (
    <div
      style={{
        marginTop: setIsAttemptingToGoNext ? "0.2rem" : "5rem",
        padding: "4rem",
      }}
    >
      <Button
        style={{
          marginBottom: "2rem",
          float: "right",
        }}
        variant="contained"
        color="primary"
        onClick={() => {
          setIsCreateSegmentModalOpen(true);
        }}
      >
        Create New Segment
      </Button>

      <CreateSegmentModal
        isOpen={isCreateSegmentModalOpen}
        shouldSetDefaultAdvertiser={!!setIsAttemptingToGoNext}
        handleClose={() => {
          setIsCreateSegmentModalOpen(false);

          setTimeout(() => {
            getSegments(selectedAdvertiser);
          }, 1000);
        }}
      />

      {!setIsAttemptingToGoNext && (
        <div
          style={{
            margin: "3rem 0",
            width: "20rem",
          }}
        >
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Select Advertiser</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={selectedAdvertiser}
              label="Advertiser"
              onChange={(e) => {
                setSelectedAdvertiser(e.target.value as string);
              }}
            >
              {advertisers.map((advertiser) => {
                return (
                  <MenuItem key={advertiser.beeswaxAdvertiserId} value={advertiser.beeswaxAdvertiserId}>
                    {advertiser.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
      )}

      {isLoading && <Loader />}

      {!isLoading && (
        <BaseDataTable
          columns={columns}
          rows={segments}
          showCheckbox={!!setIsAttemptingToGoNext}
          selectedItems={selectedSegmentIds.map((x) => String(x))}
          toggleCheckbox={(id: string) => {
            if (selectedSegmentIds.includes(id)) {
              dispatch(setSelectedSegmentIds(selectedSegmentIds.filter((x) => x !== id)));
            } else {
              dispatch(setSelectedSegmentIds([...selectedSegmentIds, id]));
            }
          }}
        />
      )}
    </div>
  );
}
